import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    Optional,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { ScorecardDataService } from '../../../core/data.service';
import { Widget, WidgetType } from '../../../core/template-definition.model';
import { YourTeamData } from '../../../core/yourteam-data.model';
import {
    autoFit,
    autoSizeColumns,
    autoSizeRows,
    bindWidthResize,
    ExperimentalNgGridComponent,
} from '../../../experimental.grid';
import { CMPS_SCORECARD_WIDGET } from '../../../scorecard.metadata';
import { PWBridge } from '../../pulse-widget-bridge.service';
import { TranslationKeyMap } from '../../pulse-widget.i18n';

import { YourTeamGridColumnId } from './your-team-grid-column-id.model';
import { createGridData } from './your-team-grid-data.function';
import { YourTeamGridData } from './your-team-grid-data.model';

@Component({
    selector: 'cmps-scorecard-yourteam-widget',
    templateUrl: './yourteam-pulse-widget.component.html',
    host: {
        class: 'cmps-scorecard-yourteam-widget',
    },
})
@TakeUntilDestroy()
export class ScYourTeamWidgetComponent implements OnInit, OnDestroy {
    @ViewChild(ExperimentalNgGridComponent)
    private readonly _grid: ExperimentalNgGridComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _YourTeamGridColumnId = YourTeamGridColumnId;
    /** @private */ _collapsed: boolean;
    /** @private */ _data: YourTeamData;
    /** @private */ _gridData: YourTeamGridData;

    constructor(
        private readonly _router: Router,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _dataService: ScorecardDataService,
        private readonly _pwBridge: PWBridge,
        @Optional()
        @Inject(CMPS_SCORECARD_WIDGET) /** @private */
        public _widget: Widget,
    ) {}

    ngOnInit(): void {
        combineLatest([
            this._dataService.observableForWidgetType<YourTeamData>(
                WidgetType.YOUR_TEAM,
            ),
            this._pwBridge.bridgeYourTeamExpandedChange(),
        ])
            .pipe(
                tap(([data, yourTeamExpanded]) => {
                    this._data = data;
                    this._gridData = createGridData(
                        this._data,
                        yourTeamExpanded ? this._data.roster.length : 2,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        bindWidthResize(this._el.nativeElement)
            .pipe(
                tap(() => this._onResize()),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        //
    }

    /** @private */
    _onUserClick(
        subordinateUserId: number,
        subordinateScorecardId: number,
    ): void {
        if (!subordinateUserId || !subordinateScorecardId) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._router.navigate(
            ['scorecards', subordinateUserId, subordinateScorecardId],
            {
                queryParams: {
                    periodTypeId: this._data.periodTypeId,
                },
            },
        );
    }

    /** @private */
    _onWillInflate(): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const el = this._el.nativeElement.querySelector(
            '[ngGrid]',
        ) as HTMLElement;
        if (el) {
            el.style.visibility = 'hidden';
        }
    }

    /** @private */
    _onDidInflate(): void {
        this._changeDetectorRef.detectChanges();
        if (!this._grid || !this._grid.renderer) {
            return;
        }

        // this is dirty. will try to improve the grid api
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._grid.renderer
            .layout()
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() =>
                autoSizeColumns(
                    this._gridData.rendererOpts.columns,
                    this._grid.renderer,
                ),
            )
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this._grid.renderer.layout())
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => autoFit(this._grid.renderer, null, 400))
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() =>
                autoSizeRows(
                    [...this._grid.renderer.contentColumns.columns.children]
                        .reverse()
                        .concat(this._grid.gridNode.children),
                    this._grid.renderer,
                    null,
                    {},
                ),
            )
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this._grid.renderer.layout())
            .catch((err) => CMPS_APP_DEF.ENV !== 'prod' && console.warn(err))
            .then(() => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                const el = this._el.nativeElement.querySelector(
                    '[ngGrid]',
                ) as HTMLElement;
                if (el) {
                    el.style.visibility = '';
                }
            });
    }

    private _onResize(): void {
        this._onWillInflate();
        this._onDidInflate();
    }
}
